import { CompetencyCriteriaUniqueness } from "types/graphql-schema";

import { UseLabelType } from "@apps/use-label/use-label";
import { assertEdgesNonNull } from "@helpers/helpers";

import { formatNameList } from "./competency-sharing";
import { describeRoleHolders } from "./helpers";

// How many competencies lose criteria when a level is deleted. The backend
// clears that level's shared criteria from every competency whose criteria
// are the same across roles, whichever section it shows in, so all of them
// count. An unsaved list stands in for what's saved.
export const countCompetenciesWithSharedCriteriaOnLevel = (
  competencies: ReadonlyArray<{
    id: number;
    criteriaUniqueness: CompetencyCriteriaUniqueness;
    criteria: ReadonlyArray<{ level?: number | null }>;
  }>,
  criteriaEdits: Record<number, ReadonlyArray<{ level?: number | null }>>,
  level: number,
) =>
  competencies.filter(
    (competency) =>
      competency.criteriaUniqueness ===
        CompetencyCriteriaUniqueness.SameAcrossRoles &&
      (criteriaEdits[competency.id] ?? competency.criteria).some(
        (criteria) => criteria.level === level,
      ),
  ).length;

export type RoleOnLevel = {
  title: string;
  trackTitle: string;
  assignedUsers: ReadonlyArray<{ id: number; name: string }>;
};

// What deleting a level takes with it. A level is the rung shared criteria
// are written against, so it only exists organization-wide: deleting one
// takes it out of every career track, wherever it's started from.
export const getLevelDeletionSummary = ({
  level,
  competencyCount,
  rolesOnLevel,
  label,
}: {
  level: number;
  competencyCount: number;
  rolesOnLevel: ReadonlyArray<RoleOnLevel>;
  label: UseLabelType;
}) => {
  const people = rolesOnLevel.flatMap((role) => role.assignedUsers);
  // Everything that goes, one line each, so it reads in one pass.
  const deleted = [
    rolesOnLevel.length > 0
      ? `${rolesOnLevel.length === 1 ? "The role" : `${rolesOnLevel.length} roles`} on it: ${formatNameList(
          rolesOnLevel.map((role) => `${role.title} (${role.trackTitle})`),
        )}. ${describeRoleHolders(people, rolesOnLevel.length)}`
      : null,
    competencyCount > 0
      ? `Level ${level} criteria from ${competencyCount} ${label("competency", {
          pluralize: competencyCount !== 1,
        })}.`
      : null,
    rolesOnLevel.length > 0 || competencyCount > 0
      ? `${label("review", {
          capitalize: true,
        })} questions and answers about those roles and criteria.`
      : null,
  ].filter((line): line is string => line !== null);
  return { title: `Delete level ${level} everywhere?`, deleted };
};

type Edges<T> = {
  edges?: readonly ({ node?: T | null } | null)[];
} | null;

// getLevelDeletionSummary, from the lists the organization's competencies and
// career tracks queries return: the one place a level's losses are counted,
// so every "Delete level" dialog says the same thing.
export const summarizeLevelDeletion = ({
  level,
  competencies,
  careerTracks,
  criteriaEdits = {},
  label,
}: {
  level: number;
  competencies: ReadonlyArray<{
    id: number;
    criteriaUniqueness: CompetencyCriteriaUniqueness;
    criteria: Edges<{ id: number; level?: number | null }>;
  }>;
  careerTracks: ReadonlyArray<{
    title: string;
    roles: Edges<{
      id: number;
      title: string;
      level: number;
      // Asked for with `withHolders` (getOrganizationCareerTracks).
      assignedUsers?: Edges<{ id: number; name: string }>;
    }>;
  }>;
  // Unsaved criteria, which stand in for what's saved.
  criteriaEdits?: Record<number, ReadonlyArray<{ level?: number | null }>>;
  label: UseLabelType;
}) =>
  getLevelDeletionSummary({
    level,
    competencyCount: countCompetenciesWithSharedCriteriaOnLevel(
      competencies.map((competency) => ({
        ...competency,
        criteria: assertEdgesNonNull(competency.criteria),
      })),
      criteriaEdits,
      level,
    ),
    rolesOnLevel: careerTracks.flatMap((track) =>
      assertEdgesNonNull(track.roles)
        .filter((role) => role.level === level)
        .map((role) => ({
          title: role.title,
          trackTitle: track.title,
          assignedUsers: assertEdgesNonNull(role.assignedUsers),
        })),
    ),
    label,
  });
